import { useCallback, useState } from 'react'
import { normaliseHex, PALETTE } from '@/design/palette'
import { labelColorOn } from '@/design/contrast'

interface ColorFieldProps {
  value: string
  onChange: (hex: string) => void
  /** Drawn inside each swatch so the choice is judged as a block, not a dot. */
  sampleText: string
}

/**
 * Palette plus free hex entry.
 *
 * The palette is what almost everyone will use; the hex box is there so a
 * specific brand or personal colour is possible. Hand-entered colours are
 * warned about when the label would be hard to read on them, but never
 * refused -- rejecting a colour someone deliberately typed is more annoying
 * than a hard-to-read block they can immediately see and change.
 */
export function ColorField({ value, onChange, sampleText }: ColorFieldProps) {
  const [hexDraft, setHexDraft] = useState('')

  const applyHex = useCallback(() => {
    const normalised = normaliseHex(hexDraft)
    if (!normalised) return
    onChange(normalised)
    setHexDraft('')
  }, [hexDraft, onChange])

  const draftValid = normaliseHex(hexDraft) !== null
  const current = normaliseHex(value) ?? value

  return (
    <div className="mg-color">
      <div className="mg-swatches" role="radiogroup" aria-label="Colour">
        {PALETTE.map((hex) => (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={hex === current}
            aria-label={hex}
            className={`mg-swatch ${hex === current ? 'mg-swatch--on' : ''}`}
            style={{ background: hex, color: labelColorOn(hex) }}
            onClick={() => onChange(hex)}
          >
            <span aria-hidden="true">{hex === current ? '✓' : ''}</span>
          </button>
        ))}
      </div>

      <div className="mg-hex">
        <label className="mg-hex-label" htmlFor="mg-hex-input">
          Or a hex colour
        </label>
        <div className="mg-hex-row">
          <input
            id="mg-hex-input"
            className="mg-input mg-hex-input"
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyHex()
              }
            }}
            placeholder={current}
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={7}
          />
          <button
            type="button"
            className="mg-btn mg-btn--inline"
            onClick={applyHex}
            disabled={!draftValid}
          >
            Use
          </button>
        </div>

        {hexDraft.trim() && !draftValid ? (
          <p className="mg-warn">Needs to look like #4A90D9.</p>
        ) : null}

        {/*
          There is deliberately no "this colour is too pale" warning.

          `labelColorOn` picks black or white at the luminance where both score
          the same contrast, so the worst any fill can manage is that crossover
          value -- and it clears AA. No hex you can type produces an unreadable
          block, which makes such a warning dead code. Asserted by sampling the
          colour cube in catalog.test.ts.
        */}
      </div>

      <div className="mg-preview-wrap">
        <span className="mg-hex-label">Preview</span>
        <div
          className="mg-preview"
          style={{ background: current, color: labelColorOn(current) }}
          aria-hidden="true"
        >
          {sampleText}
        </div>
      </div>
    </div>
  )
}
